import {
  type Connection,
  PublicKey,
  SystemProgram,
  type TransactionInstruction,
} from '@solana/web3.js';
import BN from 'bn.js';
import { MORFI_OPINION_PROGRAM_ID } from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { u64LeBuffer } from '../pda/derivations.js';
import { invokeMethod } from './common.js';

export function deriveEventScheduleAddress(
  nonce: bigint,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): PublicKey {
  if (nonce < 0n || nonce > 18_446_744_073_709_551_615n) {
    throw new RangeError('Event schedule nonce is outside u64');
  }
  const bytes = u64LeBuffer(new BN(nonce.toString()));
  return PublicKey.findProgramAddressSync(
    [Buffer.from('event_schedule'), programId.toBuffer(), bytes],
    programId,
  )[0];
}

export async function buildEventScheduleInstruction(
  connection: Connection,
  params: {
    admin: PublicKey;
    /** Rent sponsor; admin may be a multisig vault PDA. */
    payer?: PublicKey;
    marketConfig: PublicKey;
    nonce: bigint;
    start: bigint;
    end: bigint;
    expected?: { start: bigint; end: bigint };
    programId?: PublicKey;
  },
): Promise<TransactionInstruction> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  const eventSchedule = deriveEventScheduleAddress(params.nonce, programId);
  const adminConfig = PublicKey.findProgramAddressSync([Buffer.from('admin_config')], programId)[0];
  const value = (n: bigint): BN => new BN(n.toString());
  if (params.expected === undefined) {
    return invokeMethod(program, 'initEventSchedule', {
      scheduleNonce: value(params.nonce),
      tradingStartedTs: value(params.start),
      tradingEndTs: value(params.end),
    })
      .accounts({
        payer: params.payer ?? params.admin,
        configAdmin: params.admin,
        adminConfig,
        marketConfig: params.marketConfig,
        eventSchedule,
        systemProgram: SystemProgram.programId,
      })
      .instruction();
  }
  return invokeMethod(
    program,
    'updateTimedMarketSchedule',
    value(params.expected.start),
    value(params.expected.end),
    value(params.start),
    value(params.end),
  )
    .accounts({
      configAdmin: params.admin,
      adminConfig,
      eventSchedule,
      eventAuthority: PublicKey.findProgramAddressSync(
        [Buffer.from('__event_authority')],
        programId,
      )[0],
      program: programId,
    })
    .instruction();
}
